import { Info } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn';
import { Card } from './card';
import { Skeleton } from './skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip';

export type StatTone = 'neutral' | 'success' | 'warning' | 'danger';

const toneRing: Record<StatTone, string> = {
  neutral: '',
  success: 'border-success/40',
  warning: 'border-warning/50',
  danger: 'border-destructive/50',
};

const toneValue: Record<StatTone, string> = {
  neutral: '',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-destructive',
};

export interface StatCardProps {
  label: ReactNode;
  value: ReactNode;
  /** The plain reading of the value — units, a derived figure, a caveat. */
  hint?: ReactNode;
  /**
   * Colours the border and the value. Replaces the old `card.bad` class, which
   * only had one step and only existed in the admin stylesheet.
   */
  tone?: StatTone;
  /**
   * Explains the metric itself. KV-token-seconds is the reason this exists:
   * the unit is correct and unfamiliar, and an unexplained number on a billing
   * page becomes a support ticket.
   */
  explain?: ReactNode;
  icon?: ReactNode;
  loading?: boolean;
  className?: string;
}

export function StatCard({
  label,
  value,
  hint,
  tone = 'neutral',
  explain,
  icon,
  loading = false,
  className,
}: StatCardProps) {
  return (
    <Card className={cn('p-4', toneRing[tone], className)}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {label}
          </span>
          {explain && (
            <Tooltip>
              <TooltipTrigger
                type="button"
                className="rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                aria-label={`What is ${typeof label === 'string' ? label : 'this'}?`}
              >
                <Info className="size-3.5" />
              </TooltipTrigger>
              <TooltipContent>{explain}</TooltipContent>
            </Tooltip>
          )}
        </div>
        {icon && <span className="text-muted-foreground [&_svg]:size-4">{icon}</span>}
      </div>

      {loading ? (
        <Skeleton className="mt-2 h-8 w-24" />
      ) : (
        <div className={cn('mt-1.5 font-mono text-2xl font-semibold tracking-tight', toneValue[tone])}>
          {value}
        </div>
      )}

      {hint && <div className="mt-1 text-xs leading-relaxed text-muted-foreground">{hint}</div>}
    </Card>
  );
}

/** The grid stat cards sit in. Auto-fits so a row of three does not stretch. */
export function StatGrid({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn('grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4', className)}
    >
      {children}
    </div>
  );
}
